// === SHARED: windowBus ===
// A tiny, dependency-free singleton pub/sub that carries small bits of state
// BETWEEN browser windows / tabs of the same origin — so a control in ONE
// window can visibly drive a readout in ANOTHER. It is the cross-window cousin
// of audioBus: where audioBus coordinates who-owns-the-sound inside one page,
// windowBus coordinates shared key/value state across every open page.
//
// TRANSPORT: window.localStorage + the "storage" event. Writing a key to
// localStorage fires a "storage" event in EVERY OTHER same-origin window — but
// NOT in the window that wrote it. So a publish() does two things:
//   1) writes the value to localStorage (reaches OTHER windows via "storage"), and
//   2) fans the value out to local subscribers in THIS window directly,
// giving one uniform behavior whether the sender and receiver share a window or
// sit in two separate ones. State is mirrored in an in-memory map so getState()
// is instant and survives even if storage is unavailable (private mode / SSR).
//
// SHAPE ON THE WIRE: every key is namespaced under a single "az.wbus." prefix
// and stored as JSON of { v: value, t: timestamp, o: originId }. The timestamp
// makes each write distinct so re-publishing the SAME value still fires a
// "storage" event (localStorage suppresses events when the string is unchanged).
// originId lets a window ignore its own echo if one ever loops back.
//
// Design notes:
//  - No React, no DOM beyond window/localStorage, no timers. One module record.
//  - Fully null-safe: bad keys / non-function handlers / unavailable storage are
//    ignored, never thrown. publish/subscribe/getState always degrade quietly.
//  - subscribe() returns an unsubscribe function; calling it twice is harmless.
//  - A single shared "storage" listener is attached lazily on first subscribe
//    and fans incoming cross-window writes out to the matching key handlers.

// Namespace so the art site's keys can't collide with anything else in storage.
const PREFIX = "az.wbus.";

// A best-effort unique id for THIS window, used to tag writes so a window can
// recognize (and skip) its own echo if it ever comes back around.
const ORIGIN_ID =
  "w" +
  Math.random().toString(36).slice(2, 8) +
  Date.now().toString(36).slice(-4);

// key (bare, un-prefixed) -> Set<handler>. Local subscribers in THIS window.
const subscribers = new Map();

// key (bare) -> last known value. In-memory mirror so getState() is instant and
// works even when localStorage is blocked.
const state = new Map();

// Whether the single shared "storage" listener has been wired up yet.
let listening = false;

// Guard every storage touch: private mode / SSR / quota can all throw.
function safeStorage() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage;
  } catch (e) {
    // Accessing localStorage itself can throw in locked-down browsers.
    return null;
  }
}

// Fan a value out to every local handler registered for `key`. A throwing
// handler must never break its siblings or the publisher, so each is isolated.
function emitLocal(key, value) {
  const set = subscribers.get(key);
  if (!set) return;
  // Copy to an array so a handler that unsubscribes mid-iteration is safe.
  const handlers = Array.from(set);
  for (let i = 0; i < handlers.length; i++) {
    try {
      handlers[i](value);
    } catch (e) {
      // Swallow — one bad subscriber can't break the bus.
    }
  }
}

// The single cross-window listener: when ANOTHER window writes one of our keys,
// localStorage fires this here. We decode it, update our mirror, and fan it out
// to local subscribers — making the remote write look identical to a local one.
function onStorage(e) {
  if (!e || typeof e.key !== "string") return;
  if (e.key.indexOf(PREFIX) !== 0) return; // not one of ours
  const bareKey = e.key.slice(PREFIX.length);

  // A cleared key (newValue null) means the sender released it; treat as undefined.
  if (e.newValue == null) {
    state.set(bareKey, undefined);
    emitLocal(bareKey, undefined);
    return;
  }

  let parsed;
  try {
    parsed = JSON.parse(e.newValue);
  } catch (err) {
    return; // garbage / foreign write — ignore
  }
  if (!parsed || typeof parsed !== "object") return;

  // Ignore our own echo if it ever loops back to us.
  if (parsed.o === ORIGIN_ID) return;

  state.set(bareKey, parsed.v);
  emitLocal(bareKey, parsed.v);
}

// Attach the shared listener once, lazily, the first time anyone subscribes.
function ensureListening() {
  if (listening) return;
  if (typeof window === "undefined" || !window.addEventListener) return;
  window.addEventListener("storage", onStorage);
  listening = true;
}

/**
 * Subscribe to a key. `fn` is called with the key's value whenever it is
 * publish()ed from THIS window OR any other same-origin window. It is also
 * called once immediately with the current value IF one is already known, so a
 * receiver that mounts after a value was set still picks it up.
 *
 * @param {string} key     The bare key to listen on (un-prefixed).
 * @param {Function} fn     Called with (value) on each change. Non-functions ignored.
 * @returns {Function}      Unsubscribe. Idempotent and always safe to call.
 */
function subscribe(key, fn) {
  if (key == null || typeof fn !== "function") {
    return function () {};
  }
  ensureListening();

  let set = subscribers.get(key);
  if (!set) {
    set = new Set();
    subscribers.set(key, set);
  }
  set.add(fn);

  // Prime the new subscriber with the current value if we have one already.
  if (state.has(key)) {
    try {
      fn(state.get(key));
    } catch (e) {
      // ignore a throwing initial call
    }
  }

  let active = true;
  return function unsubscribe() {
    if (!active) return; // double-unsubscribe is a no-op
    active = false;
    const s = subscribers.get(key);
    if (s) {
      s.delete(fn);
      if (s.size === 0) subscribers.delete(key);
    }
  };
}

/**
 * Publish a value for `key`. Updates the in-memory mirror, writes it to
 * localStorage so OTHER windows receive it via their "storage" listener, and
 * fans it out to local subscribers in THIS window immediately.
 *
 * @param {string} key    The bare key to publish under.
 * @param {*} value        Any JSON-serializable value.
 */
function publish(key, value) {
  if (key == null) return;

  // Mirror + local fan-out first so same-window receivers update instantly,
  // independent of whether storage is available.
  state.set(key, value);
  emitLocal(key, value);

  // Then push to OTHER windows via localStorage.
  const store = safeStorage();
  if (!store) return;
  try {
    store.setItem(
      PREFIX + key,
      JSON.stringify({ v: value, t: Date.now(), o: ORIGIN_ID })
    );
  } catch (e) {
    // Quota / serialization failure — the local fan-out above still happened,
    // so same-window behavior is unaffected. Cross-window simply no-ops.
  }
}

/**
 * The last known value for `key`, or undefined if none has been seen. Reads the
 * in-memory mirror first; if empty (e.g. this window just opened and another
 * window set the value before we subscribed), it falls back to reading
 * localStorage once so a freshly-opened receiver isn't blank.
 *
 * @param {string} key
 * @returns {*} the value, or undefined.
 */
function getState(key) {
  if (key == null) return undefined;
  if (state.has(key)) return state.get(key);

  const store = safeStorage();
  if (!store) return undefined;
  try {
    const raw = store.getItem(PREFIX + key);
    if (raw == null) return undefined;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return undefined;
    state.set(key, parsed.v); // seed the mirror for next time
    return parsed.v;
  } catch (e) {
    return undefined;
  }
}

const windowBus = { subscribe, publish, getState };

export default windowBus;
